import type {
  CreateWorkerPaymentMethodType,
  CreateStaffWorkerRequest,
  EmploymentTemplate,
} from "services/api/company/staff.service";
import {
  getStockholmLocalDateNow,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";
import { toTemplateEnumOrFallback } from "../shared/staffEmploymentTemplate.logic";
import { normalizePaymentMethodValue } from "../shared/staff.paymentMethod.logic";
import type { AddStaffFormData } from "./addStaffPanel.contract";

/**
 * Creates default form state for the add staff panel.
 */
export const createInitialFormData = (): AddStaffFormData => {
  return {
    phoneNumber: "",
    employeeNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    personalIdentityNumber: "",
    role: "",
    teamIds: [],
    employmentType: undefined,
    salaryType: undefined,
    salaryAmount: "",
    workPercentage: "100",
    taxTable: "36",
    paymentMethodType: "BankAccount",
    paymentMethodValue: "",
    paymentType: "None",
    employeeCategory: "BlueCollar",
    startDate: getStockholmLocalDateNow(),
    endDate: null,
    agreement: "",
    noticeDays: 0,
    vacationDays: 0,
    workplace: "",
  };
};

const toFiniteNumberOrFallback = (
  value: string | undefined,
  fallback: number,
): number => {
  if (!value || value.trim().length === 0) return fallback;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const toWorkPercentageFractionOrZero = (value?: string): number => {
  const percentValue = toFiniteNumberOrFallback(value, 0);
  if (percentValue <= 0) return 0;
  return Math.min(percentValue / 100, 1);
};

const toNonNegativeIntegerOrZero = (value?: string | number): number => {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) return 0;
    return value >= 0 ? value : 0;
  }
  if (!value || value.trim().length === 0) return 0;
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
};

const toCreateWorkerPaymentMethodType = (
  value: AddStaffFormData["paymentMethodType"],
): CreateWorkerPaymentMethodType => {
  if (value === "InternationalBankAccount") {
    return "InternationalBankAccount";
  }

  return "bankAccount";
};

/**
 * Applies a selected employment template onto the add staff form, leaving
 * personal details and the tax table untouched.
 */
export const applyEmploymentTemplateToAddStaffForm = (
  formData: AddStaffFormData,
  template: EmploymentTemplate,
): AddStaffFormData => {
  return {
    ...formData,
    employmentType: toTemplateEnumOrFallback(
      template.employmentType,
      formData.employmentType,
    ),
    salaryType: toTemplateEnumOrFallback(
      template.salaryType,
      formData.salaryType,
    ),
    employeeCategory: toTemplateEnumOrFallback(
      template.employeeCategory,
      formData.employeeCategory,
    ),
    paymentType: toTemplateEnumOrFallback(
      template.paymentType,
      formData.paymentType,
    ),
    role: template.role ?? formData.role,
    salaryAmount:
      template.salaryValue == null
        ? formData.salaryAmount
        : String(template.salaryValue),
    workPercentage:
      template.workPercentage == null
        ? formData.workPercentage
        : String(Math.round(template.workPercentage * 100)),
    agreement: template.agreement ?? formData.agreement,
    noticeDays: template.noticeDays ?? formData.noticeDays,
    vacationDays: template.vacationDays ?? formData.vacationDays,
    workplace: template.workplace ?? formData.workplace,
  };
};

/**
 * Maps add staff form values to API payload for worker creation.
 */
export const mapAddStaffFormDataToWorkerPayload = (
  formData: AddStaffFormData,
): CreateStaffWorkerRequest => {
  const salaryType = formData.salaryType ?? "Undefined";
  const employmentType = formData.employmentType ?? "Undefined";
  const employeeCategory = formData.employeeCategory.trim() || "Undefined";
  const workPercentage =
    salaryType === "Monthly"
      ? toWorkPercentageFractionOrZero(formData.workPercentage)
      : 0;
  const startDate =
    (formData.startDate != null
      ? toIsoStartOfDayUtcFromStockholmDate(formData.startDate)
      : null) ??
    toIsoStartOfDayUtcFromStockholmDate(getStockholmLocalDateNow());
  const endDate =
    formData.endDate != null
      ? toIsoStartOfDayUtcFromStockholmDate(formData.endDate)
      : null;
  const paymentMethodValue = normalizePaymentMethodValue(
    formData.paymentMethodValue,
  );

  const payload: CreateStaffWorkerRequest = {
    worker: {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      email: formData.email.trim(),
      personalIdentityNumber: formData.personalIdentityNumber.trim(),
    },
    employment: {
      startDate,
      endDate,
      employmentType,
      employeeNumber: toNonNegativeIntegerOrZero(formData.employeeNumber),
      employeeCategory,
      salaryType,
      workPercentage,
      paymentType: formData.paymentType || "None",
      role: formData.role.trim(),
      agreement: formData.agreement.trim(),
      vacationDays: toNonNegativeIntegerOrZero(formData.vacationDays),
      noticeDays: toNonNegativeIntegerOrZero(formData.noticeDays),
      workplace: formData.workplace.trim(),
      probationPeriodEndDate: null,
      salaryValue: toFiniteNumberOrFallback(formData.salaryAmount, 0),
      taxType: "Undefined",
      taxValue: toNonNegativeIntegerOrZero(formData.taxTable),
      ...(paymentMethodValue.length > 0
        ? {
            paymentMethodType: toCreateWorkerPaymentMethodType(
              formData.paymentMethodType,
            ),
            paymentMethodValue,
          }
        : {}),
    },
    workGroupIds: formData.teamIds
      .map((id) => Number(id))
      .filter((id) => Number.isSafeInteger(id) && id >= 0),
  };

  return payload;
};
